import { __ } from '@/hub-shared/utils/i18n';
import type { EntityKey, MappingPreviewResponse, IntegrationCompaniesResponse } from '../dataSources/types';
import { fetchWithNonce } from '../utils/fetchWithNonce';
import { parseApiError } from '../utils/parseApiError';
import { SESSION_EXPIRED } from './apiErrors';

type ApiContext = {
  baseUrl: string;
  nonce: string;
};

type IntegrationCompaniesParams = {
  connectionId: string;
  q?: string;
  page?: number;
  perPage?: number;
  forceRefresh?: boolean;
};

export type EnsureCompanyResponse = {
  company_id: number;
  created: boolean;
  linked: boolean;
};

export type IntegrationImportResponse = {
  session_id: number;
  status: string;
  summary?: Record<string, unknown>;
};

const withAuthHeaders = (nonce: string): HeadersInit => ({
  'Content-Type': 'application/json',
  'X-WP-Nonce': nonce,
});

export const fetchIntegrations = async ({ baseUrl, nonce }: ApiContext): Promise<unknown[]> => {
  const response = await fetchWithNonce(`${baseUrl}integrations/list`, {
    method: 'GET',
    credentials: 'same-origin',
    headers: { 'X-WP-Nonce': nonce },
  });

  if (response.status === 401) {
    throw new Error(SESSION_EXPIRED);
  }
  if (!response.ok) {
    throw new Error(await parseApiError(response));
  }

  const payload = (await response.json()) as unknown;
  return Array.isArray(payload) ? payload : [];
};

export const fetchConnections = fetchIntegrations;

export const fetchIntegrationCompanies = async (
  { baseUrl, nonce }: ApiContext,
  params: IntegrationCompaniesParams,
): Promise<IntegrationCompaniesResponse> => {
  const searchParams = new URLSearchParams();
  if (params.q && params.q.trim().length > 0) {
    searchParams.set('q', params.q.trim());
  }
  searchParams.set('page', String(params.page ?? 1));
  searchParams.set('per_page', String(params.perPage ?? 15));
  if (params.forceRefresh) {
    searchParams.set('force_refresh', '1');
  }

  const response = await fetchWithNonce(
    `${baseUrl}integrations/${encodeURIComponent(params.connectionId)}/companies?${searchParams.toString()}`,
    {
      method: 'GET',
      credentials: 'same-origin',
      headers: { 'X-WP-Nonce': nonce },
    },
  );

  if (response.status === 401) {
    throw new Error(SESSION_EXPIRED);
  }
  if (!response.ok) {
    throw new Error(await parseApiError(response));
  }

  return (await response.json()) as IntegrationCompaniesResponse;
};

export type ImportableFieldsResponse = {
  supported_field_keys: string[];
  source: 'provider' | 'fallback';
};

export const fetchImportableFields = async (
  { baseUrl, nonce }: ApiContext,
  connectionId: string,
): Promise<ImportableFieldsResponse> => {
  const response = await fetchWithNonce(
    `${baseUrl}integrations/${encodeURIComponent(connectionId)}/profile/importable-fields`,
    {
      method: 'GET',
      credentials: 'same-origin',
      headers: { 'X-WP-Nonce': nonce },
    },
  );

  if (response.status === 401) {
    throw new Error(SESSION_EXPIRED);
  }
  if (!response.ok) {
    throw new Error(await parseApiError(response));
  }

  const payload = (await response.json()) as ImportableFieldsResponse;
  if (!payload || !Array.isArray(payload.supported_field_keys)) {
    throw new Error(__('Réponse importable-fields invalide.'));
  }

  return payload;
};

export type PreviewCompany = {
  remote_company_id: string;
  entity_keys: EntityKey[];
};

export type CabinetCompanySearchItem = {
  id: number;
  name: string;
  siren?: string;
};

export type EnsureCompanyBody = {
  entity_keys: EntityKey[];
  target_company_id?: number;
  allow_create?: boolean;
};

export const previewIntegrationMapping = async (
  { baseUrl, nonce }: ApiContext,
  connectionId: string,
  companies: PreviewCompany[],
  includeFieldKeys?: string[],
): Promise<MappingPreviewResponse> => {
  const body: Record<string, unknown> = { companies };
  if (includeFieldKeys && includeFieldKeys.length > 0) {
    body['include_field_keys'] = includeFieldKeys;
  }

  const response = await fetchWithNonce(
    `${baseUrl}integrations/${encodeURIComponent(connectionId)}/companies/preview-mapping`,
    {
      method: 'POST',
      credentials: 'same-origin',
      headers: withAuthHeaders(nonce),
      body: JSON.stringify(body),
    },
  );

  if (response.status === 401) {
    throw new Error(SESSION_EXPIRED);
  }
  if (!response.ok) {
    throw new Error(await parseApiError(response));
  }

  const payload = (await response.json()) as MappingPreviewResponse;
  if (!payload || !Array.isArray(payload.items)) {
    throw new Error(__('Réponse de mapping invalide.'));
  }

  return payload;
};

export const searchCabinetCompanies = async (
  { baseUrl, nonce }: ApiContext,
  search: string,
): Promise<CabinetCompanySearchItem[]> => {
  const params = new URLSearchParams();
  params.set('search', search.trim());
  params.set('per_page', '20');
  params.set('page', '1');

  const response = await fetchWithNonce(`${baseUrl}companies?${params.toString()}`, {
    method: 'GET',
    credentials: 'same-origin',
    headers: { 'X-WP-Nonce': nonce },
  });

  if (response.status === 401) {
    throw new Error(SESSION_EXPIRED);
  }
  if (!response.ok) {
    throw new Error(await parseApiError(response));
  }

  const payload = (await response.json()) as { companies?: unknown };
  const raw = payload.companies;
  if (!Array.isArray(raw)) {
    return [];
  }
  const out: CabinetCompanySearchItem[] = [];
  for (const row of raw) {
    if (typeof row !== 'object' || row === null) continue;
    const r = row as Record<string, unknown>;
    const id = typeof r.id === 'number' ? r.id : Number(r.id);
    const name = typeof r.name === 'string' ? r.name : '';
    if (!Number.isFinite(id) || id <= 0 || name === '') continue;
    const siren = typeof r.siren === 'string' ? r.siren : undefined;
    out.push(siren !== undefined ? { id, name, siren } : { id, name });
  }
  return out;
};

export const ensureIntegrationCompany = async (
  { baseUrl, nonce }: ApiContext,
  connectionId: string,
  remoteCompanyId: string,
  body: EnsureCompanyBody,
): Promise<EnsureCompanyResponse> => {
  const response = await fetchWithNonce(
    `${baseUrl}integrations/${encodeURIComponent(connectionId)}/companies/${encodeURIComponent(remoteCompanyId)}/ensure-company`,
    {
      method: 'POST',
      credentials: 'same-origin',
      headers: withAuthHeaders(nonce),
      body: JSON.stringify(body),
    },
  );

  if (response.status === 401) {
    throw new Error(SESSION_EXPIRED);
  }
  if (!response.ok) {
    throw new Error(await parseApiError(response));
  }

  const payload = (await response.json()) as EnsureCompanyResponse;
  if (!payload || typeof payload.company_id !== 'number' || payload.company_id <= 0) {
    throw new Error(__('Réponse ensure-company invalide.'));
  }

  return payload;
};

export const runIntegrationImport = async (
  { baseUrl, nonce }: ApiContext,
  companyId: number,
  integrationSlug: string,
  includeFieldKeys: string[],
  remoteCompanyId: string,
  remoteCompanyLabel: string,
  entityKeys: EntityKey[],
  signal?: AbortSignal,
): Promise<IntegrationImportResponse> => {
  const importBody: Record<string, unknown> = {
    remote_company_id: remoteCompanyId,
  };
  if (includeFieldKeys.length > 0) {
    importBody['include_field_keys'] = includeFieldKeys;
  }
  if (remoteCompanyLabel.trim() !== '') {
    importBody['remote_company_label'] = remoteCompanyLabel.trim();
  }
  if (entityKeys.length > 0) {
    importBody['entity_keys'] = entityKeys;
  }

  const response = await fetchWithNonce(
    `${baseUrl}company/${companyId}/profile/import/provider/${encodeURIComponent(integrationSlug)}`,
    {
      method: 'POST',
      credentials: 'same-origin',
      headers: withAuthHeaders(nonce),
      body: JSON.stringify(importBody),
      signal,
    },
  );

  if (response.status === 401) {
    throw new Error(SESSION_EXPIRED);
  }
  if (!response.ok) {
    throw new Error(await parseApiError(response));
  }

  const payload = (await response.json()) as IntegrationImportResponse;
  if (!payload || typeof payload.session_id !== 'number') {
    throw new Error(__('Réponse import invalide.'));
  }

  return payload;
};

export const fetchCompanyConflictCount = async (
  { baseUrl, nonce }: ApiContext,
  companyId: number,
): Promise<number> => {
  const response = await fetchWithNonce(`${baseUrl}company/${companyId}/profile/conflicts?per_page=1&page=1`, {
    method: 'GET',
    credentials: 'same-origin',
    headers: { 'X-WP-Nonce': nonce },
  });

  if (response.status === 401) {
    throw new Error(SESSION_EXPIRED);
  }
  if (!response.ok) {
    throw new Error(await parseApiError(response));
  }

  const payload = (await response.json()) as { total_items?: unknown };
  if (typeof payload.total_items !== 'number' || Number.isNaN(payload.total_items)) {
    throw new Error(__('Réponse conflits invalide.'));
  }

  return Math.max(0, payload.total_items);
};
